import React, { useEffect, useState } from 'react';
import { listProcessSignatures } from '../../api/admin';
import Spinner from '../../components/ui/Spinner';

function normalizeRows(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.content)) return d.content;
  if (Array.isArray(d?.signatures)) return d.signatures;
  return [];
}

/**
 * SUPER_ADMIN: read-only process signature / blocklist table.
 */
export default function ProcessSignaturesPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError('');
      setLoading(true);
      try {
        const res = await listProcessSignatures();
        if (cancelled) return;
        setRows(normalizeRows(res));
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || 'Failed to load process signatures.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="page-pad page-y flex justify-center py-24">
        <Spinner size="md" label="Loading blocklist…" />
      </div>
    );
  }

  if (error) {
    return <div className="page-pad page-y text-sm text-red-600">{error}</div>;
  }

  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full space-y-6">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Platform admin</p>
        <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Process blocklist</h1>
        <p className="text-sm text-ink-muted mt-0.5">
          Signed process entries used for integrity checks (read-only).
        </p>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-canvas-subtle border-b border-line">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-muted uppercase tracking-wider">Process</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-muted uppercase tracking-wider">Hash</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-muted uppercase tracking-wider">Risk level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-12 text-center text-ink-muted text-sm">No signatures returned.</td>
                </tr>
              ) : (
                rows.map((row, i) => {
                  const key = row.id ?? row.hash ?? i;
                  const name = row.processName ?? row.name ?? row.process ?? '—';
                  const hash = row.hash ?? row.sha256 ?? row.signature ?? '—';
                  const risk = row.riskLevel ?? row.risk ?? row.severity ?? '—';
                  return (
                    <tr key={key} className="hover:bg-canvas-subtle/80 transition-colors">
                      <td className="px-4 py-3 font-medium text-ink">{name}</td>
                      <td className="px-4 py-3 text-xs font-mono text-ink-muted break-all max-w-md">{hash}</td>
                      <td className="px-4 py-3 text-ink-secondary">{risk}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
